import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import "./dashboard.css";
import Instance from "../../AxiosConfig";
import { Spin, Segmented } from "antd";

const TotalClosureGraph = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [chartData, setChartData] = useState({
    labels: monthNames,
    managers: [],
  });
  const [selectedYear, setSelectedYear] = useState("");

  const [manualMax, setManualMax] = useState(""); // NEW FIELD

  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState("closure");



  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(currentYear.toString());
  }, []);

  // ================================
  // CHART RENDER
  // ================================
  useLayoutEffect(() => {
    if (!chartRef.current) return;
    const ctx = chartRef.current.getContext("2d");

    // ensure values is always an array
    const allValues = chartData.managers.flatMap((manager) =>
      viewMode === "closure"
        ? manager.closure
        : manager.achievement
    );

    const maxValue = Math.max(0, ...allValues);

    const DEFAULT_Y_MAX =
      viewMode === "achievement"
        ? 200
        : 1000000;

    let yMax;


    if (manualMax) {
      yMax = Number(manualMax);
    } else {
      const calculatedMax =
        viewMode === "achievement"
          ? Math.ceil(Math.max(100, maxValue))
          : Math.ceil(maxValue * 1.1 / 100000) * 100000;

      yMax = Math.max(DEFAULT_Y_MAX, calculatedMax);
    }
    const stepSize = Math.ceil(yMax / 5);

    const lineData = {
      labels: chartData.labels,
      datasets: chartData.managers.map((manager) => ({
        label: manager.label,
        data:
          viewMode === "closure"
            ? manager.closure
            : manager.achievement,
        backgroundColor: manager.backgroundColor,
        borderColor: manager.borderColor,
        borderWidth: 1,
        borderRadius: 6,
        categoryPercentage: 0.7,
        barPercentage: 0.9,
        barThickness: window.innerWidth < 768 ? 16 : 20,
        maxBarThickness: window.innerWidth < 768 ? 16 : 20,
      })),
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          stacked: false,
          beginAtZero: true,
          max: yMax,
          ticks: {
            stepSize: stepSize,
            callback: (value) =>
              viewMode === "achievement"
                ? `${value}%`
                : Number(value).toLocaleString("en-IN"),
            color: "#9ca3af",
            font: { size: 14 },
          },
          grid: {
            color: "rgba(0,0,0,0.1)",
            drawBorder: false,
            drawTicks: false,
          },
        },
        x: {
          stacked: false,
          ticks: {
            color: "#64748B",
            font: {
              size: window.innerWidth < 768 ? 10 : 12,
            },
            maxRotation: window.innerWidth < 768 ? 25 : 45,
            minRotation: window.innerWidth < 768 ? 25 : 0,
            autoSkip: false,
          },
          grid: { display: false },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const manager = chartData.managers.find(
                (m) => m.label === context.dataset.label
              );

              if (!manager) return "";

              const monthIndex = context.dataIndex;

              if (viewMode === "closure") {
                return `${manager.label}: ₹${Number(
                  manager.closure[monthIndex] || 0
                ).toLocaleString("en-IN")}`;
              }

              return [
                `${manager.label}`,
                `Achievement : ${manager.achievement[monthIndex] || 0}%`,
                `Closure : ₹${Number(
                  manager.closure[monthIndex] || 0
                ).toLocaleString("en-IN")}`,
                `Target : ₹${Number(
                  manager.target[monthIndex] || 0
                ).toLocaleString("en-IN")}`,
              ];
            },
          },
        },
      },
    };
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: lineData,
      options,
    });

    return () => {
      chartInstanceRef.current?.destroy();
    };

  }, [chartData, manualMax, viewMode]);

  // ================================
  // FETCH CHART DATA
  // ================================
  const fetchChartData = async () => {
    if (!selectedYear) return;

    setLoading(true);

    try {
      const response = await Instance.get(
        "/dashboard/teams-monthwise-closure",
        {
          params: {
            year: selectedYear,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        const monthWise = response.data.monthWise || {};

        const managersMap = {};

        monthNames.forEach((month) => {
          const teams = monthWise[month] || [];

          teams.forEach((team) => {
            if (!managersMap[team.managerId]) {
              managersMap[team.managerId] = {
                label: team.managerName,
                backgroundColor: team.barColor,
                borderColor: team.barColor,
                closure: new Array(12).fill(0),
                target: new Array(12).fill(0),
                achievement: new Array(12).fill(0),
              };
            }

            const index = monthNames.indexOf(month);

            managersMap[team.managerId].closure[index] =
              team.team.closure || 0;

            managersMap[team.managerId].target[index] =
              team.team.target || 0;

            managersMap[team.managerId].achievement[index] =
              team.team.achievement || 0;
          });
        });

        setChartData({
          labels: monthNames,
          managers: Object.values(managersMap),
        });
      }
    } catch (err) {
      console.error(err);

      setChartData({
        labels: monthNames,
        managers: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [selectedYear]);


  return (
    <div className="chart-card mt-4">
      <div className="chart-header">
        <h2 className="chart-title">Monthly Overall Performance</h2>

        <div className="chart-dropdowns">
          <select
            className="chart-dropdown"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="" disabled>
              Select Year
            </option>
            {Array.from({ length: 2050 - 2025 + 1 }, (_, i) => 2025 + i).map(
              (year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              )
            )}
          </select>

          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              {
                label: "Closure",
                value: "closure",
              },
              {
                label: "Achievement %",
                value: "achievement",
              },
            ]}
          />
          {/* Manual Max Y-Axis */}
          <input
            type="number"
            className="chart-dropdown"
            placeholder={
              viewMode === "achievement"
                ? "Max Percentage"
                : "Enter Max Amount"
            }
            value={manualMax}
            onChange={(e) => setManualMax(e.target.value)}
          />
        </div>
      </div>

      <div
        className="chart-body"
        style={{ position: "relative", minHeight: 300 }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "8px",
            }}
          >
            <Spin size="large" />
          </div>
        )}

        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default TotalClosureGraph;
